// framework
import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { sleep } from "utils/sleep";
import { audio } from "services/audioManager";
import { shuffle } from "utils/arrays";

//styles
import styles from "./RevealView.module.scss";

// assets
import { IMAGES, MESSAGES } from "constants/constants";
import { SFX, MUSIC, VO } from "constants/audio"

// components
import CashSlot, { buildCashMatrix } from "components/Host/SubComponents/CashSlot/CashSlot";
import CashRain from "components/Host/SubComponents/CashRain/CashRain";

// constants
import { GAME_STATES } from "constants/constants";

const roundBackgroundMap = {
    1: { bgBlue: IMAGES.HOST.revealBackgroundBlue4, bgRed: IMAGES.HOST.revealBackgroundRed4, answerCount: 4 },
    2: { bgBlue: IMAGES.HOST.revealBackgroundBlue4, bgRed: IMAGES.HOST.revealBackgroundRed4, answerCount: 4 },
    3: { bgBlue: IMAGES.HOST.revealBackgroundBlue4, bgRed: IMAGES.HOST.revealBackgroundRed4, answerCount: 4 },
    4: { bgBlue: IMAGES.HOST.revealBackgroundBlue3, bgRed: IMAGES.HOST.revealBackgroundRed3, answerCount: 3 },
    5: { bgBlue: IMAGES.HOST.revealBackgroundBlue3, bgRed: IMAGES.HOST.revealBackgroundRed3, answerCount: 3 },
    6: { bgBlue: IMAGES.HOST.revealBackgroundBlue3, bgRed: IMAGES.HOST.revealBackgroundRed3, answerCount: 3 },
    7: { bgBlue: IMAGES.HOST.revealBackgroundBlue2, bgRed: IMAGES.HOST.revealBackgroundRed2, answerCount: 2 },
};

const dropShaftMap = {
    4: { 0: IMAGES.HOST.revealDropShaft_4_1, 1: IMAGES.HOST.revealDropShaft_4_2, 2: IMAGES.HOST.revealDropShaft_4_3, 3: IMAGES.HOST.revealDropShaft_4_4 },
    3: { 0: IMAGES.HOST.revealDropShaft_3_1, 1: IMAGES.HOST.revealDropShaft_3_2, 2: IMAGES.HOST.revealDropShaft_3_3 },
    2: { 0: IMAGES.HOST.revealDropShaft_2_1, 1: IMAGES.HOST.revealDropShaft_2_2 },
};

const dropDelayRoundMapInMs = {
    1: { startDelay: 1000, dropDelayRange: [300, 800] },
    2: { startDelay: 3000, dropDelayRange: [1500, 3000] },
    3: { startDelay: 6000, dropDelayRange: [3000, 4000] },
    4: { startDelay: 8000, dropDelayRange: [4000, 5000] },
    5: { startDelay: 10000, dropDelayRange: [6000, 8000] },
    6: { startDelay: 12000, dropDelayRange: [8000, 10000] },
    7: { startDelay: 14000, dropDelayRange: [10000, 12000] },
};

const CASH_RETURN_DELAY = 1000;
const CASH_RETURN_TICK_MS = 140;
const AFTER_ALL_DROPPED_MS = 2000;

function getRandomDelay(roundNumber) {
    const delayConfig = dropDelayRoundMapInMs[roundNumber] || dropDelayRoundMapInMs[1];
    const [min, max] = delayConfig.dropDelayRange;
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

async function runRevealSequence({ isReveal, slotCount, correctIndex, tallestPerSlot, totalPerSlot, setDropped, setCashStackHeight, setFlashKey, sendIfPrimary, signal, roundNumber }) {
    if (!isReveal || slotCount === 0 || signal.aborted) return;

    setDropped(new Set());
    setCashStackHeight(tallestPerSlot);
    setFlashKey(0);
    await audio.playVo(VO.BRING_ON_THE_DROP, { signal });

    const delayConfig = dropDelayRoundMapInMs[roundNumber] || dropDelayRoundMapInMs[1];
    await sleep(delayConfig.startDelay);
    if (signal.aborted) return;

    const all = [...Array(slotCount).keys()];
    const incorrect = all.filter(i => i !== correctIndex && i < slotCount);
    const shuffled = shuffle(incorrect);
    const order = shuffled.sort((a, b) => totalPerSlot[a] - totalPerSlot[b]);

    let levels = [...tallestPerSlot];

    for (let idx = 0; idx < order.length; idx++) {
        const i = order[idx];
        if (signal.aborted) return;

        setDropped(prev => {
            const next = new Set(prev);
            next.add(i);
            return next;
        });
        levels[i] = 0;
        setCashStackHeight([...levels]);

        // Trigger flash with unique key
        setFlashKey(idx + 1);
        audio.playSfx(SFX.MONEY_DROPPED, { signal });

        if (idx < order.length - 1) {
            const dropDelay = getRandomDelay(roundNumber);
            await sleep(dropDelay);
            if (signal.aborted) return;
        }
    }

    await sleep(CASH_RETURN_DELAY);

    // Calculate total cash dropped and start playing appropriate reaction VO unless last round
    let voPromise = null;
    if (!signal.aborted && roundNumber !== 7) {
        const totalDropped = order.reduce((sum, slotIdx) => sum + totalPerSlot[slotIdx], 0);
        const totalCash = totalPerSlot.reduce((sum, val) => sum + val, 0);
        const percentageDropped = totalCash > 0 ? (totalDropped / totalCash) : 0;

        // Start playing positive or negative reaction (don't await yet)
        if (percentageDropped === 1) {
            //if loss no vo
        } else if (percentageDropped < 0.5) {
            voPromise = audio.playVo(VO.POSITIVE_DROP, { signal });
        } else {
            voPromise = audio.playVo(VO.NEGATIVE_DROP, { signal });
        }
    }

    // Run cash return animation while VO plays
    if (signal.aborted) return;
    const droppedSet = new Set(order);
    const survivors = all.filter(i => !droppedSet.has(i));
    if (survivors.length > 0) {
        while (!signal.aborted && survivors.some(i => levels[i] > 0)) {
            await sleep(CASH_RETURN_TICK_MS);
            levels = levels.map((v, i) =>
                droppedSet.has(i) ? 0 : Math.max(0, v - 1)
            );
            setCashStackHeight([...levels]);
        }
    }

    await sleep(AFTER_ALL_DROPPED_MS);

    // Wait for VO to finish if it's still playing
    if (voPromise && !signal.aborted) {
        await voPromise;
    }

    if (!signal.aborted) {
        sendIfPrimary?.(MESSAGES.TO_SERVER.REVEAL_COMPLETE);
    }
}


export default function RevealView({ gameState, room, roomState, players, sendIfPrimary }) {
    const [dropped, setDropped] = useState(new Set());
    const [flashKey, setFlashKey] = useState(0);
    const [cashStackHeight, setCashStackHeight] = useState([]);

    const isReveal = gameState === GAME_STATES.REVEAL;
    const showSection = isReveal;

    const roundNumber = roomState?.mdmdData?.roundNumber ?? 1;
    const roundConfig = roundBackgroundMap[roundNumber] || roundBackgroundMap[1];

    const backgroundBlue = roundConfig.bgBlue;
    const backgroundRed = roundConfig.bgRed;
    const slotCount = roundConfig.answerCount;
    const answerSlots = useMemo(() => Array.from({ length: slotCount }), [slotCount]);

    const answers = roomState?.mdmdData?.answers ?? [];
    const correctAnswer = roomState?.mdmdData?.correctAnswer;
    const correctIndex = answers.findIndex(a => a === correctAnswer);

    const hasAnyDropped = dropped.size > 0;

    const longestAnswerLen = useMemo(() => {
        if (!answers || !answers.length) return 0;
        return answers.reduce((max, a) => Math.max(max, (a ?? "").trim().length), 0);
    }, [answers]);
    const showSmallAnswers = longestAnswerLen > 25;

    // Build cash matrix using the exported function from CashSlot
    const cashMatrix = useMemo(() => {
        return buildCashMatrix(players, slotCount);
    }, [players, slotCount]);

    const tallestPerSlot = useMemo(() => {
        return Array.from({ length: slotCount }, (_, slotIdx) => {
            const stacksInSlot = cashMatrix[slotIdx] ?? [];
            return Math.max(0, ...stacksInSlot.map(s => s.count));
        });
    }, [slotCount, cashMatrix]);

    const totalPerSlot = useMemo(() => {
        return Array.from({ length: slotCount }, (_, slotIdx) => {
            const stacksInSlot = cashMatrix[slotIdx] ?? [];
            const totalNotes = stacksInSlot.reduce((sum, s) => sum + s.count, 0);
            return totalNotes * 25000;
        });
    }, [slotCount, cashMatrix]);

    // Initialize cash stack heights at start of reveal
    useEffect(() => { if (isReveal) setCashStackHeight(tallestPerSlot); }, [isReveal, tallestPerSlot]);

    // Reset visuals when we leave REVEAL (or unmount)
    useEffect(() => {
        if (!isReveal) {
            setDropped(new Set());
            setFlashKey(0);
            setCashStackHeight([]);
        }
    }, [isReveal]);

    // Run reveal sequence — cancellable
    useEffect(() => {
        const ac = new AbortController();
        runRevealSequence({ isReveal, slotCount, correctIndex, tallestPerSlot, totalPerSlot, setDropped, setCashStackHeight, setFlashKey, sendIfPrimary, signal: ac.signal, roundNumber });
        return () => ac.abort();
    }, [isReveal, slotCount, correctIndex, sendIfPrimary, roundNumber]);

    // Play background music while this component is visible
    useEffect(() => {
        if (!showSection) return;
        const ac = new AbortController();
        audio.playMusic(MUSIC.REVEAL_LOOP, { loop: true, fadeMs: 500, signal: ac.signal, });
        return () => ac.abort();
    }, [showSection]);

    return (
        <section className={styles.revealview} data-show={showSection ? "true" : "false"} data-testid="reveal-view">
            <div className={styles.bgWrap}>
                <img src={backgroundBlue} className={`${styles.bg} ${!hasAnyDropped ? styles.show : ""}`} alt="" />
                <img src={backgroundRed} className={`${styles.bg} ${hasAnyDropped ? styles.show : ""}`} alt="" />
            </div>
            <img key={`flash-left-${flashKey}`} src={IMAGES.HOST.revealLeftFlash} alt="" aria-hidden="true" className={`${styles.flash} ${styles.left} ${flashKey > 0 ? styles.flashActive : ""}`} />
            <img key={`flash-right-${flashKey}`} src={IMAGES.HOST.revealRightFlash} alt="" aria-hidden="true" className={`${styles.flash} ${styles.right} ${flashKey > 0 ? styles.flashActive : ""}`} />
            <div className={styles.answersRoot} data-count={slotCount}>
                {answerSlots.map((_, slotIdx) => (
                    <div key={`ans-${slotIdx}`} className={`${styles.panel} ${styles.answerTile}`} style={{ backgroundImage: `url(${IMAGES.HOST.answerBackground})` }}>
                        <span className={`${styles.answerLabel} ${showSmallAnswers ? styles.small : ""}`}>{answers[slotIdx] ?? ""}</span>
                        <img src={IMAGES.HOST.droppedAnswer} alt="" aria-hidden="true" className={styles.droppedAnswer} data-show={dropped.has(slotIdx) ? "true" : "false"} />
                    </div>
                ))}
            </div>
            <div className={styles.cashRoot} data-count={slotCount}>
                {answerSlots.map((_, slotIdx) => {
                    const slotIsDropped = dropped.has(slotIdx);
                    const baseLevel = tallestPerSlot[slotIdx] ?? 0;
                    const slotLevel = slotIsDropped ? 0 : (cashStackHeight[slotIdx] ?? baseLevel);
                    return (
                        <CashSlot key={`cash-slot-${slotIdx}`} className={styles.cashSlot} slotIdx={slotIdx} slotCount={slotCount} cashMatrix={cashMatrix}
                            stackLevel={slotLevel} gameState={gameState} cashGap = "5%" maxCashWidth = "15%" />
                    );
                })}
            </div>

            <div className={styles.shaftsRoot} data-count={slotCount}>
                {answerSlots.map((_, slotIdx) => (
                    <div key={`shaft-${slotIdx}`} className={styles.shaftWrap}>
                        <img className={styles.droppedShaft} src={dropShaftMap[slotCount][slotIdx]} alt="" data-show={dropped.has(slotIdx) ? "true" : "false"} />
                        <CashRain className={styles.dropZone} data-slot={slotIdx} active={dropped.has(slotIdx)} slotIdx={slotIdx} slotCount={slotCount}
                            cashMatrix={cashMatrix} gameState={GAME_STATES.REVEAL} maxPerPlayer={10} durationMs={1000} maxDelayMs={200} />
                    </div>
                ))}
            </div>
        </section>
    );
}

RevealView.propTypes = {
    gameState: PropTypes.any,
    room: PropTypes.object,
    roomState: PropTypes.object,
    players: PropTypes.array
};